'use client'

import * as React from 'react'
import { DataTable } from '@/components/data-table/data-table'
import { useDataTable } from '@/hooks/use-data-table'
import { DataTableSortList } from '@/components/data-table/data-table-sort-list'

import { useTranslationsTableColumns } from './translations-table-columns'
import { TranslationsTableActions } from './translations-table-actions'
import { TranslationsTableActionBar } from './translations-table-action-bar'
import { TranslationsTableSkeleton } from './TranslationsTableSkeleton'
import { MainTableState } from '@/lib/utils/table-state-utils'
import type { TableConfig } from '@/types/data-table'
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table'
import { TranslationListItem } from '@/lib/api/translations'
import { DeleteTranslationModal } from './delete-translation-modal'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

interface TranslationsTableProps {
	translations: TranslationListItem[]
	total: number
	loading: boolean
	onTableStateChange: (state: MainTableState) => void
	onRefetch: () => void
	config?: TableConfig
}

/**
 * TranslationsTable component for displaying translations with DataTable functionality
 */
export function TranslationsTable({
	translations,
	total,
	loading,
	onTableStateChange,
	onRefetch,
	config = {},
}: TranslationsTableProps) {
	const { storeRoutes, navigate, id: storeId } = useStoreNavigate()
	const [translationId, setTranslationId] = React.useState<string | undefined>(undefined)

	const columns = useTranslationsTableColumns({
		storeId: storeId,
		onEditTranslation: (id: string) => {
			// Navigate to translation details page if route exists
			const translationDetailsRoute = storeRoutes.TRANSLATION_EDIT?.(id)
			if (translationDetailsRoute) {
				navigate(translationDetailsRoute)
			}
		},
		onDeleteTranslation: (id: string) => setTranslationId(id),
	})

	const { table } = useDataTable({
		data: translations,
		columns,
		total,
		config,
		onTableStateChange,
		getRowId: (row: TranslationListItem) => row.id,
	})

	return (
		<DataTable
			table={table}
			actionBar={<TranslationsTableActionBar table={table} />}
			isLoading={loading}
			skeletonRows={<TranslationsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
		>
			<TranslationsTableActions table={table} onRefresh={onRefetch} isLoading={loading} storeId={storeId} />
			<DataTableSortList table={table} align="end" />
			<DeleteTranslationModal translationId={translationId} onClose={() => setTranslationId(undefined)} />
		</DataTable>
	)
}

